'use client';

import { useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import GoogleAuthButton from '@/components/auth/GoogleAuthButton';
import TurnstileWidget, {
  type TurnstileWidgetHandle,
} from '@/components/auth/TurnstileWidget';
import { startGoogleLogin } from '@/lib/oauth';
import { safeLocalRedirect, syncAulaAccount } from '@/lib/firstRunAuth';
import { Alert, Button, Input } from '@/components/ui';
import LoginForm from './LoginForm';

export default function TeacherSignInSplit() {
  const router = useRouter();
  const params = useSearchParams();
  const supabase = useSupabaseClient();
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null);

  const incomingMode = String(params.get('mode') || '').trim().toLowerCase();
  const nextOverride = params.get('next') || undefined;
  const incomingCode = String(params.get('code') || '').trim();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  if (incomingMode === 'signup' || incomingCode) {
    return <LoginForm role="teacher" />;
  }

  const destination = safeLocalRedirect(nextOverride, '/teacher');

  function requireCaptchaToken() {
    const token = captchaToken || turnstileRef.current?.getToken() || null;
    if (!token) {
      setError('Security check is still loading. Please wait a moment and try again.');
      return null;
    }
    return token;
  }

  function resetCaptcha() {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  }

  async function syncBootstrapFromClientSession() {
    const { data } = await supabase.auth.getSession();
    return syncAulaAccount(data?.session);
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const token = requireCaptchaToken();
    if (!token) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken: token },
      });
      if (error) {
        setError(error.message);
        return;
      }
      const setup = await syncBootstrapFromClientSession();
      const created = Date.parse(setup.auth_created_at || '');
      const firstLogin = setup.created_personal_org === true || (Number.isFinite(created) && Date.now() - created >= 0 && Date.now() - created < 30 * 60_000);
      router.replace(firstLogin ? '/teacher' : destination);
      router.refresh();
    } catch {
      setError('We could not finish setting up your Aula workspace. Please try signing in again.');
    } finally {
      resetCaptcha();
      setSubmitting(false);
    }
  }

  async function handleForgotPassword() {
    if (forgotSubmitting) return;
    setError(null);
    setInfo(null);

    const normalizedEmail = String(email || '').trim();
    if (!normalizedEmail) {
      setError('Enter your email first, then click Forgot password.');
      return;
    }

    const token = requireCaptchaToken();
    if (!token) return;

    setForgotSubmitting(true);
    try {
      const origin = window.location.origin;
      const redirectTo = `${origin}/auth/reset?next=${encodeURIComponent(destination)}`;
      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo,
        captchaToken: token,
      });
      if (error) {
        setError(error.message);
        return;
      }
      setInfo('Check your email for a password reset link.');
    } finally {
      resetCaptcha();
      setForgotSubmitting(false);
    }
  }

  async function signInWithGoogle() {
    if (googleSubmitting) return;
    setError(null);
    setInfo(null);
    setGoogleSubmitting(true);
    try {
      await startGoogleLogin('teacher', destination);
    } catch (e: any) {
      setError(e?.message || 'Google sign-in failed. Please try again.');
      setGoogleSubmitting(false);
    }
  }

  return (
    <div className="w-full py-3 sm:py-6">
      <div className="mx-auto max-w-lg rounded-aula-lg border border-aula-border bg-white p-5 shadow-aula-card sm:p-7">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-950">Teacher Sign In</h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">Sign in to continue to Aula.</p>
        </div>

        {error ? <Alert tone="danger" className="mt-5">{error}</Alert> : null}
        {info ? <Alert tone="success" className="mt-5">{info}</Alert> : null}

        <div className="mt-6">
          <GoogleAuthButton
            onClick={signInWithGoogle}
            loading={googleSubmitting}
            disabled={googleSubmitting}
            label="Continue with Google"
            loadingLabel="Opening Google…"
          />
          <p className="mt-2 text-center text-xs text-slate-500">Use your school or personal Google account.</p>
        </div>

        <div className="my-6 flex items-center gap-3" aria-hidden="true">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">or continue with email</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={handleLogin}>
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-slate-800">Email</span>
              <Input className="w-full" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-slate-800">Password</span>
              <div className="relative">
                <Input className="w-full pr-20" type={showPw ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
                <button type="button" onClick={() => setShowPw((value) => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-600 hover:text-slate-900">
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </label>

            <div className="flex justify-end">
              <button type="button" onClick={handleForgotPassword} disabled={forgotSubmitting || !captchaToken} className="text-sm font-medium text-blue-700 hover:underline disabled:opacity-60">
                {forgotSubmitting ? 'Sending…' : 'Forgot password?'}
              </button>
            </div>

            <div className="overflow-x-auto">
              <TurnstileWidget ref={turnstileRef} onTokenChange={setCaptchaToken} />
            </div>

            <Button type="submit" size="lg" className="w-full" loading={submitting} disabled={submitting || !captchaToken}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </div>
        </form>

        <div className="mt-6 border-t border-slate-200 pt-5 text-center">
          <p className="text-sm font-semibold text-slate-800">New to Aula?</p>
          <Button type="button" variant="secondary" size="lg" className="mt-3 w-full" onClick={() => router.push('/login/teacher?mode=signup')}>
            Create a free Aula account
          </Button>
          <p className="mt-2 text-xs text-slate-500">No credit card required.</p>
        </div>
      </div>
    </div>
  );
}
